import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageBySlug, getGalleryBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function Brunsviger() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    async function loadData() {
      const [page, gallery] = await Promise.all([
        getPageBySlug('brunsviger'),
        getGalleryBySlug('bageri') // Fallback to bageri gallery if no specific brunsviger gallery
      ]);
      setPageData(page);
      setGalleryData(gallery);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Fynsk Brunsviger · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Få den helt rigtige fynske brunsviger hos Vissenbjerg Bageri. Vi bager brunsviger kagemand/kagedame og festlige bryllupsbrunsvigere. Bestil nu.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Fyns Nationalspise
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Den Fynske Brunsviger
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Her får du den rigtige, fynske brunsviger
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Den lækre, bløde dejbund og det søde, cremede og karameliserede farinfyld – brunsvigeren er Fyns fantastiske gave til alle Danmarks søde tænder. Og så stammer brunsvigeren måske slet ikke fra Fyn, selv om den har fundet sit hjem her.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Hos Vissenbjerg Bageri er det gode fynske bagerhåndværk i højsædet, og vi sætter en ære i at bage den helt rigtige fynske brunsviger – i alle former og til alle formål, fra basserne til morgenbordet eller kagebordet til den flotte kage til bryllupsfesten.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vores populære brunsviger kagemand/kagedame fås i to størrelser, til 15 eller 25 personer. Den kommer vidt omkring – vi har for eksempel leveret kagemand til Christiansborg. Og vi er specialister i den festlige bryllupsbrunsviger.
            </p>
            <div className="pt-2">
              <a
                href="https://brunsvigermand.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-chocolate/5 border border-gold-brown/30 px-4 py-2.5 text-sm font-semibold text-chocolate hover:bg-gold-brown hover:text-cream transition-all duration-200 group"
              >
                <span>Besøg vores dedikerede side:</span>
                <span className="text-gold-brown group-hover:text-cream font-bold underline underline-offset-2">brunsvigermand.dk</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl overflow-hidden shadow-md h-48">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=300,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-kagemand.jpg"
                alt="Brunsviger Kagemand"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-md h-48">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,height=300,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-kagedame.jpg"
                alt="Brunsviger Kagedame"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-md h-48 col-span-2">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,height=400,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-bryllupskage-top.jpg"
                alt="Brunsviger Bryllupskage"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>

        {/* History Section */}
        <div className="bg-chocolate text-cream rounded-2xl p-8 md:p-12 shadow-xl border border-gold-brown/20 mb-20">
          <div className="max-w-3xl mx-auto space-y-6">
            <h3 className="font-serif text-2xl font-bold text-gold-brown">Fra Braunschweig til Vissenbjerg?</h3>
            <p className="text-sm text-cream/80 leading-relaxed">
              Som det ofte er med gode historier, så er der flere udgaver af brunsvigerens historie. Måske stammer den fra Braunschweig. I hvert fald kendte man i 1800-tallets Tyskland en Braunschweiger Dickkuchen, der mindede om brunsvigeren. En anden historie går ud på, at der ved Brylle nær Vissenbjerg var en bebyggelse, der hed Brunsvig.
            </p>
            <p className="text-sm text-cream/80 leading-relaxed">
              I nærheden lå et teglværk, hvor der helt tilbage i 1500-tallet var ansat tyske arbejdere. Måske har de bagt brunsvigere?
            </p>
            <p className="text-sm text-cream/80 leading-relaxed">
              I hvert fald fik mange danskere mulighed for at bage denne type kager, da jernkomfuret vandt indpas omkring 1850, og man kunne bage Hamborgkage og altså Brunsvigerkage, som blev omtalt i Madam Mangors kogebog i 1837. Siden blev den altså til den fynske brunner.
            </p>
            <p className="text-sm text-cream/80 leading-relaxed">
              Herfra har den bredt sig til resten af landet. Men som enhver brunsvigerkender ved, så er den helt rigtige brunsviger fra en fynsk bager.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center space-y-6 max-w-xl mx-auto">
          <h3 className="font-serif text-2xl font-bold text-chocolate">Bestil din brunsviger i dag</h3>
          <p className="text-sm text-chocolate/80">
            Skal du bruge en brunsviger kagemand, kagedame eller en festlig bryllupsbrunsviger til dit næste arrangement? Ring og bestil direkte hos os.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://brunsvigermand.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-gold-brown px-6 py-3 text-sm font-semibold text-cream hover:bg-copper transition-colors duration-200 shadow-md"
            >
              Bestil på brunsvigermand.dk <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
            </a>
            <a
              href="tel:64471266"
              className="inline-flex items-center gap-2 rounded-md border-2 border-gold-brown px-6 py-3 text-sm font-semibold text-gold-brown hover:bg-gold-brown hover:text-cream transition-all duration-200"
            >
              <i className="fa-solid fa-phone" /> Ring 64 47 12 66
            </a>
            <Link
              to="/bestilling"
              className="inline-flex items-center gap-2 rounded-md border-2 border-gold-brown/40 bg-white/60 px-6 py-3 text-sm font-semibold text-chocolate hover:border-gold-brown hover:text-gold-brown transition-all duration-200"
            >
              Læs om bestilling <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
